import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Users as UsersIcon, Activity, Settings, Shield, LogOut, Plus, Trash2,
  ShieldCheck, ShieldOff, UserCircle2, Mail, X, Clock, Globe, AlertTriangle,
  CheckCircle2, RotateCcw, KeyRound, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../components/ConfirmProvider";
import { ALL_PERMISSIONS } from "../lib/permissions";
import { TrackingSettingsCard } from "../components/tracking/TrackingSettingsCard";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Pełen dostęp do wszystkiego" },
  { value: "koordynator", label: "Koordynator", desc: "Flota, trasy, doręczenia" },
  { value: "ksiegowa", label: "Księgowa", desc: "Tylko finanse" },
  { value: "kierowca", label: "Kierowca", desc: "Tylko ekran QR — używa appki mobilnej" },
];

const PERMISSION_LABELS = {
  dashboard: "Pulpit",
  fleet: "Flota & GPS",
  routes: "Planer Tras",
  deliveries: "Doręczenia",
  finance: "Finanse",
  contracts: "Umowy + Pracownicy",
  employees: "Pracownicy CRUD",
  users: "Lista użytkowników",
  admin: "Panel administratora",
  hr: "Kadry (teczki, dokumenty, rachunki)",
};

const DEFAULT_PERMISSIONS_BY_ROLE = {
  admin:       [...ALL_PERMISSIONS],
  koordynator: ["fleet", "routes", "deliveries"],
  ksiegowa:    ["finance"],
  kierowca:    [],
  tester:      [...ALL_PERMISSIONS],
};

const STATUS_META = {
  active: { label: "Aktywny", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  invited: { label: "Zaproszony", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  pending: { label: "Do zatwierdzenia", cls: "bg-red-100 text-red-800 border-red-300" },
  disabled: { label: "Zablokowany", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
};

const EMPTY = { email: "", name: "", role: "ksiegowa" };

const TABS = [
  { id: "users", label: "Konta", icon: UsersIcon },
  { id: "sessions", label: "Sesje", icon: Activity },
  { id: "system", label: "System", icon: Settings },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s temu`;
  if (s < 3600) return `${Math.floor(s / 60)} min temu`;
  if (s < 86400) return `${Math.floor(s / 3600)} h temu`;
  return d.toLocaleDateString("pl-PL");
}
function untilExpiry(iso) {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "wygasła";
  const d = Math.floor(ms / 86400000);
  if (d > 0) return `${d} dni`;
  const h = Math.floor(ms / 3600000);
  return `${h} h`;
}

export default function AdminPage() {
  const { user: me } = useAuth();
  const { confirm, promptInput } = useConfirm();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showAdminCreate, setShowAdminCreate] = useState(false);
  const [permsUser, setPermsUser] = useState(null); // user object whose perms we're editing
  const [emailLive, setEmailLive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, s, ss, es] = await Promise.all([
        api.get("/users").then((r) => r.data),
        api.get("/admin/stats").then((r) => r.data),
        api.get("/admin/sessions").then((r) => r.data),
        api.get("/admin/email-status").then((r) => r.data).catch(() => null),
      ]);
      setUsers(u);
      setStats(s);
      setSessions(ss);
      setEmailLive(es);
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    try {
      const res = await api.post("/users", form);
      const emailMsg = res.data.invitation_email_sent_at
        ? ` — wysłano zaproszenie email`
        : res.data.invitation_email_error
        ? ` — UWAGA: email nie wysłany (${res.data.invitation_email_error.slice(0, 80)})`
        : "";
      toast.success(`Zaproszono ${form.email}${emailMsg}`);
      setForm(EMPTY);
      setShowInvite(false);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const resendInvitation = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/resend-invitation`);
      toast.success(`Wysłano ponownie zaproszenie do ${u.email}`);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const sendTestEmail = async () => {
    const target = await promptInput(
      "Wyślij testową wiadomość na adres:",
      me?.email || ""
    );
    if (!target) return;
    try {
      await api.post("/admin/email-test", { to_email: target });
      toast.success(`Test wysłany na ${target} — sprawdź skrzynkę`);
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}`, { role });
      toast.success(`Rola zmieniona na ${role}`);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const toggleStatus = async (u) => {
    const newStatus = u.status === "disabled" ? "active" : "disabled";
    try {
      await api.put(`/users/${u.user_id}`, { status: newStatus });
      toast.success(newStatus === "disabled" ? "Konto zablokowane" : "Konto odblokowane");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const remove = async (u) => {
    if (!(await confirm(
      `Usunąć użytkownika ${u.email || u.username}? Wszystkie sesje zostaną wyczyszczone.`,
      { tone: "danger", confirmLabel: "Usuń" }
    ))) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("Użytkownik usunięty");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const approve = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/approve`);
      toast.success(`Zatwierdzono: ${u.name || u.email || u.username}`);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const revertToPending = async (u) => {
    if (!(await confirm(
      `Cofnąć aktywację użytkownika ${u.email || u.username}?\n\n` +
      `Konto wróci do stanu „Do zatwierdzenia", uprawnienia zostaną wyczyszczone, ` +
      `a wszystkie aktywne sesje zostaną zakończone.`,
      { tone: "warn", confirmLabel: "Cofnij aktywację" }
    ))) return;
    try {
      await api.post(`/users/${u.user_id}/revert-to-pending`);
      toast.success(`Cofnięto aktywację: ${u.name || u.email || u.username}`);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const forceLogout = async (u) => {
    if (!(await confirm(
      `Wymusić wylogowanie ${u.email || u.username} ze wszystkich urządzeń?`,
      { tone: "warn", confirmLabel: "Wyloguj" }
    ))) return;
    try {
      const r = await api.post(`/users/${u.user_id}/force-logout`);
      toast.success(`Unieważniono ${r.data.sessions_revoked} sesji`);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-label mb-1">// Administracja</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
            Panel administratora
          </h1>
          <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
            Zarządzanie kontami, sesjami i bezpieczeństwem aplikacji. Logowanie odbywa się przez
            Google — hasła są zarządzane przez konta Google (reset odbywa się tam).
          </p>
        </div>
        {tab === "users" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setForm(EMPTY);
                setShowInvite(true);
              }}
              data-testid="btn-invite-user"
              className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover transition-colors"
            >
              <Plus className="w-4 h-4" />
              Zaproś użytkownika
            </button>
            <button
              onClick={() => setShowAdminCreate(true)}
              data-testid="btn-admin-create-user"
              className="inline-flex items-center gap-2 px-5 py-3 font-bold uppercase tracking-wider border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
              title="Utwórz konto z loginem + hasłem (pominięcie Google + zaproszenia mailowego)"
            >
              <Plus className="w-4 h-4" />
              Utwórz konto + hasło
            </button>
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-900">
        <Kpi label="Wszyscy użytkownicy" value={stats?.users_total ?? "—"} testId="kpi-users-total" />
        <Kpi
          label="Aktywne konta"
          value={stats?.by_status?.active ?? 0}
          sub={`zaproszeni: ${stats?.by_status?.invited ?? 0}`}
          dark
          testId="kpi-users-active"
        />
        <Kpi
          label="Aktywne sesje"
          value={stats?.sessions_active ?? 0}
          sub="zalogowanych użytkowników"
          testId="kpi-sessions-active"
        />
        <Kpi
          label="Administratorzy"
          value={stats?.by_role?.admin ?? 0}
          sub={`księgowa: ${stats?.by_role?.ksiegowa ?? 0} · kierowca: ${stats?.by_role?.kierowca ?? 0}`}
          dark
          testId="kpi-admins"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-300 flex gap-0" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors -mb-px border-b-2 ${
              tab === t.id
                ? "border-brand text-brand"
                : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* USERS TAB */}
      {tab === "users" && (
        <div className="space-y-6">
          <div className="border border-zinc-900 overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="bg-zinc-900 text-white text-left">
                  <Th>Użytkownik</Th>
                  <Th>Rola</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Akcje</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-zinc-500 text-sm">
                      Ładowanie...
                    </td>
                  </tr>
                )}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-zinc-500 text-sm">
                      Brak użytkowników.
                    </td>
                  </tr>
                )}
                {users.map((u) => {
                  const isMe = u.user_id === me?.user_id;
                  const statusMeta = STATUS_META[u.status] || STATUS_META.active;
                  return (
                    <tr
                      key={u.user_id}
                      className="border-t border-zinc-200 hover:bg-zinc-50"
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img
                              src={u.picture}
                              alt=""
                              className="w-8 h-8 rounded-full border border-zinc-300"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center">
                              <UserCircle2 className="w-5 h-5 text-zinc-500" />
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-sm">
                              {u.name || u.email.split("@")[0]}
                              {isMe && (
                                <span className="ml-2 text-[10px] font-mono text-brand">(TY)</span>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          data-testid={`role-select-${u.user_id}`}
                          value={u.role}
                          disabled={isMe}
                          onChange={(e) => changeRole(u, e.target.value)}
                          className="border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-sm focus:outline-none focus:border-brand disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 border text-xs font-mono ${statusMeta.cls}`}>
                          {statusMeta.label}
                        </span>
                        {u.status === "invited" && u.invitation_email_sent_at && (
                          <div className="text-[10px] font-mono text-emerald-700 mt-1 flex items-center gap-1">
                            <Mail className="w-2.5 h-2.5" /> wysłano
                          </div>
                        )}
                        {u.status === "invited" && u.invitation_email_error && (
                          <div
                            className="text-[10px] font-mono text-red-700 mt-1 flex items-center gap-1"
                            title={u.invitation_email_error}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> błąd
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        {!isMe && (
                          <>
                            {u.status === "pending" && (
                              <button
                                onClick={() => approve(u)}
                                data-testid={`btn-approve-${u.user_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 mr-1.5"
                                title="Zatwierdź konto"
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                Zatwierdź
                              </button>
                            )}
                            {u.status === "active" && (
                              <button
                                onClick={() => revertToPending(u)}
                                data-testid={`btn-revert-${u.user_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white mr-1.5"
                                title="Cofnij aktywację — konto wróci do stanu oczekującego"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Cofnij aktywację
                              </button>
                            )}
                            {u.status === "invited" && (
                              <button
                                onClick={() => resendInvitation(u)}
                                data-testid={`btn-resend-${u.user_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-brand hover:text-brand mr-1.5"
                                title="Wyślij ponownie email z zaproszeniem"
                              >
                                <Mail className="w-3 h-3" />
                                {u.invitation_email_sent_at ? "Ponów" : "Wyślij email"}
                              </button>
                            )}
                            <button
                              onClick={() => setPermsUser(u)}
                              data-testid={`btn-perms-${u.user_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-emerald-600 hover:text-emerald-700 mr-1.5"
                              title="Uprawnienia — dodaj lub zabierz dostęp do sekcji portalu"
                            >
                              <KeyRound className="w-3 h-3" />
                              Uprawnienia
                              <span className="font-mono text-emerald-700">· {(u.permissions || []).length}</span>
                            </button>
                            <button
                              onClick={() => forceLogout(u)}
                              data-testid={`btn-force-logout-${u.user_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-amber-600 hover:text-amber-700 mr-1.5"
                              title="Wymuś wylogowanie ze wszystkich urządzeń"
                            >
                              <LogOut className="w-3 h-3" />
                              Wyloguj
                            </button>
                            <button
                              onClick={() => toggleStatus(u)}
                              data-testid={`btn-toggle-${u.user_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-1.5"
                              title={u.status === "disabled" ? "Odblokuj" : "Zablokuj"}
                            >
                              {u.status === "disabled" ? (
                                <ShieldCheck className="w-3 h-3" />
                              ) : (
                                <ShieldOff className="w-3 h-3" />
                              )}
                              {u.status === "disabled" ? "Odblokuj" : "Zablokuj"}
                            </button>
                            <button
                              onClick={() => remove(u)}
                              data-testid={`btn-delete-user-${u.user_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-900">
            {ROLES.map((r, i) => (
              <div
                key={r.value}
                className={`p-5 border-r border-zinc-900 last:border-r-0 ${
                  i % 2 === 1 ? "bg-zinc-900 text-white" : "bg-white"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4" />
                  <div className="font-label">{r.label}</div>
                </div>
                <div className="text-sm">{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SESSIONS TAB */}
      {tab === "sessions" && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 px-5 py-3 flex items-start gap-3 text-sm">
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-900">Sesje wygasają automatycznie po 7 dniach</div>
              <div className="text-amber-800 mt-1">
                Aby wymusić natychmiastowe wylogowanie — wybierz &bdquo;Wyloguj&rdquo; przy koncie w zakładce &bdquo;Konta&rdquo;,
                albo usuń wybraną sesję poniżej.
              </div>
            </div>
          </div>
          <div className="border border-zinc-900 overflow-x-auto">
            <table className="w-full" data-testid="sessions-table">
              <thead>
                <tr className="bg-zinc-900 text-white text-left">
                  <Th>Użytkownik</Th>
                  <Th>Rola</Th>
                  <Th>Token</Th>
                  <Th>Zalogowany</Th>
                  <Th>Wygasa za</Th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-zinc-500 text-sm">
                      Brak aktywnych sesji.
                    </td>
                  </tr>
                )}
                {sessions.map((s, i) => (
                  <tr
                    key={s.user_id + "-" + i}
                    className="border-t border-zinc-200 hover:bg-zinc-50"
                    data-testid={`session-row-${s.user_id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {s.picture ? (
                          <img src={s.picture} alt="" className="w-6 h-6 rounded-full" />
                        ) : (
                          <UserCircle2 className="w-5 h-5 text-zinc-400" />
                        )}
                        <div>
                          <div className="font-semibold text-sm">{s.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-xs uppercase">{s.role}</td>
                    <td className="py-3 px-4 font-mono text-xs text-zinc-500">
                      {s.session_token_prefix}
                    </td>
                    <td className="py-3 px-4 font-mono text-xs">
                      <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                      {timeAgo(s.created_at)}
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-zinc-500">
                      {untilExpiry(s.expires_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SYSTEM TAB */}
      {tab === "system" && (
        <div className="space-y-6">
          <TrackingSettingsCard />
          <section className="border border-zinc-900 p-6">
            <div className="font-label mb-2">// Wysyłka emaili</div>
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <h3 className="font-heading font-black text-xl uppercase tracking-tight mb-4">
                  Powiadomienia email
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-label text-[10px]">Status</span>
                    <span
                      className={`inline-block px-2 py-0.5 border text-xs font-mono ${
                        emailLive?.login_ok
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : stats?.email_configured
                          ? "bg-rose-100 text-rose-800 border-rose-300"
                          : "bg-amber-100 text-amber-800 border-amber-300"
                      }`}
                      data-testid="email-config-status"
                    >
                      {emailLive?.login_ok
                        ? "AKTYWNE ✓"
                        : stats?.email_configured
                        ? "ODRZUCONE PRZEZ GOOGLE"
                        : "BRAK KONFIGURACJI"}
                    </span>
                  </div>
                  {stats?.email_configured && (
                    <div className="flex items-center gap-2">
                      <span className="font-label text-[10px]">Nadawca</span>
                      <span className="font-mono text-sm text-zinc-800">{stats.email_sender}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="font-label text-[10px]">Provider</span>
                    <span className="font-mono text-sm text-zinc-800">Gmail SMTP (smtp.gmail.com:587)</span>
                  </div>
                </div>
                {emailLive && !emailLive.login_ok && emailLive.env_configured && (
                  <div
                    data-testid="email-error-banner"
                    className="mt-4 p-3 border-l-2 border-rose-600 bg-rose-50 text-[11px] text-rose-900"
                  >
                    <div className="font-bold mb-1">⚠ Google odrzuca App Password</div>
                    <div className="font-mono break-all">{emailLive.error}</div>
                    <div className="mt-2 text-[10px] text-rose-700">
                      Wygeneruj NOWY App Password na{" "}
                      <a href="https://myaccount.google.com/apppasswords"
                         target="_blank" rel="noreferrer"
                         className="underline font-bold">
                        myaccount.google.com/apppasswords
                      </a>{" "}
                      i prześlij do podmiany w <code className="bg-rose-100 px-1">backend/.env</code>.
                    </div>
                  </div>
                )}
                <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
                  Konfiguracja w pliku <code className="bg-zinc-100 px-1.5">backend/.env</code>:{" "}
                  <code className="bg-zinc-100 px-1.5">GMAIL_SENDER</code> +{" "}
                  <code className="bg-zinc-100 px-1.5">GMAIL_APP_PASSWORD</code>. App Password generuje się
                  w ustawieniach konta Google (wymaga 2FA).
                </p>
              </div>
              {stats?.email_configured && (
                <button
                  onClick={sendTestEmail}
                  data-testid="btn-email-test"
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-brand text-white px-4 py-3 font-bold uppercase tracking-wider text-xs transition-colors flex-shrink-0"
                >
                  <Mail className="w-4 h-4" />
                  Test wysyłki
                </button>
              )}
            </div>
          </section>

          <EmailIngestSection />


          <section className="border border-zinc-900 p-6">
            <div className="font-label mb-2">// Autoryzacja</div>
            <h3 className="font-heading font-black text-xl uppercase tracking-tight mb-4">
              Email administratorów
            </h3>
            <p className="text-sm text-zinc-600 mb-4">
              Te konta otrzymują rolę <strong>admin</strong> automatycznie przy pierwszym logowaniu przez Google.
              Zmiana wymaga edycji zmiennej środowiskowej <code className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5">ADMIN_EMAILS</code> w pliku backend/.env.
            </p>
            <ul className="space-y-2" data-testid="admin-emails-list">
              {(stats?.admin_emails || []).map((e) => (
                <li key={e} className="font-mono text-sm flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-brand" /> {e}
                </li>
              ))}
            </ul>
          </section>

          <section className="border border-zinc-900 p-6">
            <div className="font-label mb-2">// Firma</div>
            <h3 className="font-heading font-black text-xl uppercase tracking-tight mb-4">
              Dane firmy
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <InfoRow label="Nazwa" value="GOPOSSIBLE SP. Z O.O." />
              <InfoRow label="NIP" value="8513329264" />
              <InfoRow label="REGON" value="541516390" />
              <InfoRow label="KRS" value="0001168989" />
              <InfoRow label="Adres" value="ul. Cyryla i Metodego 13/14, 71-541 Szczecin" />
              <InfoRow label="Email" value="gopossiblebiuro@gmail.com" />
            </div>
            <p className="mt-5 text-[11px] font-mono text-zinc-400">
              // Edycja danych firmy w panelu — wkrótce. Obecnie wartości zaszyte w kodzie aplikacji.
            </p>
          </section>

          <section className="border border-zinc-900 p-6">
            <div className="font-label mb-2">// Bezpieczeństwo</div>
            <h3 className="font-heading font-black text-xl uppercase tracking-tight mb-4">
              Zasady dostępu
            </h3>
            <ul className="space-y-2 text-sm text-zinc-700">
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                Logowanie wyłącznie przez Google — bez haseł zarządzanych w aplikacji.
              </li>
              <li className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                Sesje przechowywane jako httpOnly cookie, ważne 7 dni.
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                Rejestracja zamknięta — tylko administrator może zapraszać nowe osoby.
              </li>
              <li className="flex items-start gap-2">
                <LogOut className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                Admin może wymusić wylogowanie wybranego użytkownika ze wszystkich urządzeń (zakładka &bdquo;Konta&rdquo;).
              </li>
              <li className="flex items-start gap-2">
                <ShieldOff className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                Admin może natychmiast zablokować konto (status &bdquo;Zablokowany&rdquo; → kolejne żądania → 403).
              </li>
            </ul>
          </section>
        </div>
      )}

      {/* Invite modal */}
      {showInvite && (
        <div
          className="fixed inset-0 z-[1100] bg-black/40 flex items-stretch justify-end"
          onClick={() => setShowInvite(false)}
          data-testid="invite-overlay"
        >
          <form
            onSubmit={invite}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md h-full border-l border-zinc-900 p-8 overflow-y-auto"
            data-testid="invite-form"
          >
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="font-label">// Zaproszenie</div>
                <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
                  Zaproś użytkownika
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="p-2 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">
              <Field label="Email Google" required>
                <input
                  type="email"
                  required
                  data-testid="invite-email"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="np. jan.kowalski@gmail.com"
                />
              </Field>
              <Field label="Imię i nazwisko">
                <input
                  data-testid="invite-name"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field label="Rola">
                <select
                  data-testid="invite-role"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label} — {r.desc}
                    </option>
                  ))}
                </select>
              </Field>

              <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-zinc-200">
                <input
                  type="checkbox"
                  data-testid="invite-send-email"
                  className="mt-1 w-4 h-4 accent-brand"
                  checked={form.send_email}
                  onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                />
                <span className="text-sm">
                  <span className="font-bold flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> Wyślij email z zaproszeniem
                  </span>
                  <span className="text-xs text-zinc-500 block mt-1">
                    {stats?.email_configured
                      ? `Wiadomość zostanie wysłana z ${stats.email_sender}`
                      : "Email niewyłączony — skontaktuj się z administratorem systemu."}
                  </span>
                </span>
              </label>
            </div>

            <div className="mt-10 flex gap-3">
              <button
                type="submit"
                data-testid="btn-send-invite"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover"
              >
                <Mail className="w-4 h-4" />
                Zaproś
              </button>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
              >
                Anuluj
              </button>
            </div>

            <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
              Po zaproszeniu osoba może wejść na stronę logowania i zalogować się przez Google
              używając podanego adresu email. Status zmieni się z &bdquo;Zaproszony&rdquo; na &bdquo;Aktywny&rdquo;.
            </p>
          </form>
        </div>
      )}

      {/* Admin direct-create user (with password + permissions) */}
      {showAdminCreate && (
        <AdminCreateUserModal
          onClose={() => setShowAdminCreate(false)}
          onCreated={(u) => {
            setUsers((prev) => [u, ...prev]);
            setShowAdminCreate(false);
            toast.success(`Konto „${u.username}" utworzone (rola: ${u.role}, uprawnienia: ${u.permissions?.length || 0})`);
          }}
        />
      )}

      {/* Edit permissions for existing user */}
      {permsUser && (
        <UserPermissionsModal
          user={permsUser}
          onClose={() => setPermsUser(null)}
          onSaved={(updated) => {
            setUsers((prev) => prev.map((x) => x.user_id === updated.user_id ? updated : x));
            setPermsUser(null);
            toast.success(`Zapisano uprawnienia (${updated.permissions?.length || 0})`);
          }}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-label block mb-2">
        {label}
        {required && <span className="text-brand ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function Kpi({ label, value, sub, dark, testId }) {
  return (
    <div
      data-testid={testId}
      className={`p-5 border-r border-zinc-900 last:border-r-0 ${
        dark ? "bg-zinc-900 text-white" : "bg-white text-zinc-900"
      }`}
    >
      <div className={`font-label mb-3 ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
        {label}
      </div>
      <div className="font-heading font-black text-3xl">{value}</div>
      {sub && (
        <div className={`text-[11px] font-mono mt-1 ${dark ? "text-zinc-400" : "text-zinc-500"}`}>
          {sub}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="font-label text-[10px]">{label}</span>
      <span className="font-mono text-sm text-zinc-800">{value}</span>
    </div>
  );
}


function AdminCreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    username: "", password: "", name: "", email: "", phone: "",
    role: "kierowca",
  });
  const [perms, setPerms] = useState(DEFAULT_PERMISSIONS_BY_ROLE.kierowca);
  const [busy, setBusy] = useState(false);

  const onRoleChange = (role) => {
    setForm((f) => ({ ...f, role }));
    setPerms(DEFAULT_PERMISSIONS_BY_ROLE[role] ? [...DEFAULT_PERMISSIONS_BY_ROLE[role]] : []);
  };

  const togglePerm = (p) => {
    setPerms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users/admin-create", { ...form, permissions: perms });
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się utworzyć konta");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}
      data-testid="admin-create-modal-backdrop"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-2 border-zinc-900 max-w-2xl w-full p-8 shadow-[12px_12px_0_0_#18181b] my-8"
        data-testid="admin-create-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-label mb-1">// Nowe konto</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
              Utwórz konto z hasłem
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Konto utworzone bezpośrednio (status: active). Nie wysyłamy emaila.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <Field label="Login" required>
            <input data-testid="ac-username" required minLength={4}
                   value={form.username}
                   onChange={(e) => setForm({ ...form, username: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 focus:border-zinc-900 outline-none" />
          </Field>
          <Field label="Hasło" required>
            <input data-testid="ac-password" type="password" required minLength={8}
                   value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   placeholder="min 8 znaków"
                   className="w-full border border-zinc-300 px-3 py-2 focus:border-zinc-900 outline-none" />
          </Field>
          <Field label="Imię i nazwisko" required>
            <input data-testid="ac-name" required value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 focus:border-zinc-900 outline-none" />
          </Field>
          <Field label="Email (opc.)">
            <input data-testid="ac-email" type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 focus:border-zinc-900 outline-none" />
          </Field>
          <Field label="Telefon (opc.)">
            <input data-testid="ac-phone" value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 focus:border-zinc-900 outline-none" />
          </Field>
          <Field label="Rola" required>
            <select data-testid="ac-role" value={form.role}
                    onChange={(e) => onRoleChange(e.target.value)}
                    className="w-full border border-zinc-300 px-3 py-2 focus:border-zinc-900 outline-none bg-white">
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="border-t border-zinc-200 pt-5">
          <div className="font-label mb-3">// Uprawnienia ({perms.length}/{ALL_PERMISSIONS.length})</div>
          <p className="text-[11px] text-zinc-500 mb-3">
            Możesz dowolnie zmienić uprawnienia w stosunku do domyślnych dla wybranej roli.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ac-permissions-grid">
            {ALL_PERMISSIONS.map((p) => (
              <label key={p}
                     data-testid={`ac-perm-${p}`}
                     className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-xs transition-colors ${
                       perms.includes(p)
                         ? "border-brand bg-brand/5 font-bold"
                         : "border-zinc-200 hover:border-zinc-900"
                     }`}>
                <input type="checkbox" checked={perms.includes(p)}
                       onChange={() => togglePerm(p)}
                       className="accent-brand" />
                <span>{PERMISSION_LABELS[p] || p}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-8">
          <button type="button" onClick={onClose} disabled={busy}
                  className="px-5 py-3 border-2 border-zinc-300 font-bold uppercase tracking-wider text-sm hover:border-zinc-900">
            Anuluj
          </button>
          <button type="submit" disabled={busy}
                  data-testid="ac-submit"
                  className="px-5 py-3 bg-brand text-white font-bold uppercase tracking-wider text-sm hover:bg-brand-hover disabled:opacity-60">
            {busy ? "Tworzę..." : "Utwórz konto"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AdminCreateUserModal_END_OF_FILE() {
  return null;
}

// ═════════════════════════════════════════════════════════════════════════
// UserPermissionsModal — edit an existing user's permissions in-place.
// Preserves the current permission set, lets admin flip individual keys or
// snap-to-role defaults. Saves via PUT /users/{id} which accepts arbitrary
// arrays validated against ALL_PERMISSIONS on the backend.
// ═════════════════════════════════════════════════════════════════════════
function UserPermissionsModal({ user, onClose, onSaved }) {
  const [perms, setPerms] = useState(new Set(user.permissions || []));
  const [busy, setBusy] = useState(false);

  const toggle = (p) => {
    setPerms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const setAll = (checked) => {
    setPerms(new Set(checked ? ALL_PERMISSIONS : []));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/users/${user.user_id}`, {
        permissions: Array.from(perms),
      });
      onSaved?.(data);
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const roleDefaults = {
    admin: ALL_PERMISSIONS,
    tester: ALL_PERMISSIONS,
    koordynator: ["fleet", "routes", "deliveries", "schedule"],
    ksiegowa: ["finance"],
    kierowca: ["dashboard", "deliveries", "schedule"],
  };
  const applyRoleDefaults = () => {
    const defaults = roleDefaults[user.role] || [];
    setPerms(new Set(defaults));
    toast.info(`Ustawiono domyślne dla roli „${user.role}" (${defaults.length})`);
  };

  const originalSet = new Set(user.permissions || []);
  const changed =
    perms.size !== originalSet.size ||
    ALL_PERMISSIONS.some((p) => perms.has(p) !== originalSet.has(p));

  return (
    <div
      className="fixed inset-0 z-[1200] bg-black/40 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="user-perms-modal"
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg h-full border-l border-zinc-900 p-6 overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-label">// Uprawnienia</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
              {user.name || user.email}
            </h2>
            <div className="text-xs text-zinc-500 mt-1 font-mono">
              {user.email} · rola: {user.role}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-zinc-100"
            data-testid="user-perms-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Bulk actions */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={applyRoleDefaults}
            data-testid="user-perms-role-defaults"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border-2 border-brand text-brand hover:bg-brand hover:text-white"
          >
            <Shield className="w-3.5 h-3.5" /> Domyślne dla „{user.role}"
          </button>
          <button
            type="button"
            onClick={() => setAll(true)}
            data-testid="user-perms-all"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Zaznacz wszystkie
          </button>
          <button
            type="button"
            onClick={() => setAll(false)}
            data-testid="user-perms-none"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-red-600 hover:text-red-600"
          >
            <ShieldOff className="w-3.5 h-3.5" /> Wyczyść
          </button>
        </div>

        {/* Permissions grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="user-perms-grid">
          {ALL_PERMISSIONS.map((p) => {
            const checked = perms.has(p);
            return (
              <label
                key={p}
                data-testid={`user-perm-${p}`}
                className={`flex items-center gap-2 px-3 py-2.5 border cursor-pointer text-sm transition-colors ${
                  checked
                    ? "border-brand bg-brand/5 font-bold"
                    : "border-zinc-200 hover:border-zinc-900"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p)}
                  className="accent-brand w-4 h-4"
                />
                <div className="flex-1">
                  <div>{PERMISSION_LABELS[p] || p}</div>
                  <div className="text-[10px] font-mono text-zinc-400">{p}</div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="mt-4 text-[11px] font-mono text-zinc-500">
          // Zaznaczonych: {perms.size} / {ALL_PERMISSIONS.length}
          {changed && <span className="text-amber-600"> · niezapisane zmiany</span>}
        </div>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-2.5 border-2 border-zinc-300 font-bold uppercase tracking-widest text-xs hover:border-zinc-900"
          >
            Anuluj
          </button>
          <button
            type="submit"
            disabled={busy || !changed}
            data-testid="user-perms-save"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white font-bold uppercase tracking-widest text-xs hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            {busy ? "Zapisuję..." : "Zapisz uprawnienia"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EmailIngestSection() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const load = async () => {
    try {
      const d = await api.get("/email-ingest/status").then((r) => r.data);
      setState(d);
    } catch (err) {
      toast.error("Nie udało się załadować statusu: " + (err.response?.data?.detail || err.message));
    }
  };
  useEffect(() => { load(); }, []);

  const pollNow = async () => {
    setLoading(true);
    try {
      const r = await api.post("/email-ingest/poll").then((res) => res.data);
      toast.success(`Sprawdzono skrzynkę: ${r.processed || 0} plików, ${r.created || 0} nowych kosztów`);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const addEmail = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Niepoprawny adres");
    const emails = Array.from(new Set([...(state?.whitelist || []), e]));
    await api.put("/email-ingest/whitelist", { emails });
    setNewEmail("");
    load();
    toast.success("Dodano do whitelisty");
  };

  const removeEmail = async (e) => {
    const emails = (state?.whitelist || []).filter((x) => x !== e);
    await api.put("/email-ingest/whitelist", { emails });
    load();
    toast.success("Usunięto z whitelisty");
  };

  return (
    <section className="border border-zinc-900 p-6" data-testid="email-ingest-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="font-label mb-2">// Automatyzacja</div>
          <h3 className="font-heading font-black text-xl uppercase tracking-tight mb-2">
            Import faktur z maili (IMAP)
          </h3>
          <p className="text-sm text-zinc-600 mb-4">
            Polling co 5 min skrzynki. Załączniki PDF od nadawców z whitelisty są parsowane
            przez Gemini 2.5 Flash i tworzone jako koszty w KUP ze statusem <strong>planowany</strong>.
            Powiadomienie ląduje na dashboardzie.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
            <StatusCard label="Status" value={state?.enabled ? "AKTYWNY" : "WYŁĄCZONY"} tone={state?.enabled ? "ok" : "warn"} testId="ei-status" />
            <StatusCard label="Ostatnie sprawdzenie" value={state?.last_poll_at ? new Date(state.last_poll_at).toLocaleString("pl-PL") : "—"} tone={state?.last_poll_ok ? "ok" : "warn"} testId="ei-last-poll" />
            <StatusCard label="Przetworzone" value={state?.processed_count ?? 0} testId="ei-processed" />
            <StatusCard label="Utworzone koszty" value={state?.created_count ?? 0} testId="ei-created" />
          </div>

          {state?.last_error && (
            <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-xs mb-4">
              Ostatni błąd: {state.last_error}
            </div>
          )}

          <div className="mb-4">
            <div className="font-label text-xs mb-2">// Whitelist nadawców</div>
            <ul className="space-y-1 mb-2" data-testid="ei-whitelist">
              {(state?.whitelist || []).map((e) => (
                <li key={e} className="font-mono text-sm flex items-center gap-2 group">
                  <span className="flex-1">{e}</span>
                  <button
                    onClick={() => removeEmail(e)}
                    data-testid={`ei-remove-${e}`}
                    className="text-red-600 hover:text-red-800 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                  >
                    usuń
                  </button>
                </li>
              ))}
              {(state?.whitelist || []).length === 0 && (
                <li className="text-zinc-400 italic text-sm">Brak wpisów</li>
              )}
            </ul>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="dodaj@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addEmail()}
                data-testid="ei-new-email"
                className="flex-1 border border-zinc-300 bg-white px-3 py-2 font-mono text-sm focus:outline-none focus:border-zinc-900"
              />
              <button
                onClick={addEmail}
                data-testid="ei-add-email"
                className="px-4 py-2 bg-zinc-900 text-white text-xs font-bold uppercase tracking-wider hover:bg-brand"
              >
                Dodaj
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={pollNow}
          disabled={loading}
          data-testid="ei-poll-now"
          className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-brand text-white px-4 py-3 font-bold uppercase tracking-wider text-xs transition-colors flex-shrink-0 disabled:opacity-50"
        >
          <Mail className="w-4 h-4" />
          {loading ? "Sprawdzam..." : "Sprawdź teraz"}
        </button>
      </div>
    </section>
  );
}

function StatusCard({ label, value, tone, testId }) {
  const toneCls = tone === "ok" ? "border-emerald-500 text-emerald-700" : tone === "warn" ? "border-amber-500 text-amber-700" : "border-zinc-300 text-zinc-700";
  return (
    <div className={`border p-2 ${toneCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-mono opacity-70">{label}</div>
      <div className="font-mono font-bold truncate">{value}</div>
    </div>
  );
}

